import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle2, XCircle, Phone, MapPin, FileBadge, Loader2,
  ShieldCheck, ShieldX, Edit3, ExternalLink, Copy, FileWarning,
  CreditCard, Image as ImageIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const copy = (v) => { navigator.clipboard.writeText(String(v || '')); toast.success('Copied'); };

const STATUS_BADGE = {
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  active: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-rose-100 text-rose-800 border-rose-300',
  blocked: 'bg-zinc-200 text-zinc-800 border-zinc-400',
  documents_pending: 'bg-orange-100 text-orange-800 border-orange-300',
};

// Resolve an image source — OG app may push either a data URL,
// a base64 blob, or a remote URL.
const resolveImage = (raw) => {
  if (!raw) return null;
  if (typeof raw !== 'string') return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('http')) return raw;
  return `data:image/jpeg;base64,${raw}`;
};

function KycDocPreview({ label, src, onMarkUnclear }) {
  const [zoom, setZoom] = useState(false);
  if (!src) {
    return (
      <div className="border rounded p-4 text-center text-slate-400 text-sm">
        <ImageIcon className="w-6 h-6 mx-auto mb-1" /> {label} not uploaded
      </div>
    );
  }
  return (
    <>
      <div className="border rounded overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50 text-xs">
          <span className="font-medium text-slate-700">{label}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setZoom(true)} data-testid={`kyc-zoom-${label}`}>
              <ImageIcon className="w-3.5 h-3.5 mr-1" /> View
            </Button>
            <a href={src} download={`${label.replace(/\s+/g, '_')}.jpg`}>
              <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5" /></Button>
            </a>
            <Button size="sm" variant="outline" className="text-amber-700 border-amber-300"
                    onClick={onMarkUnclear} data-testid={`kyc-unclear-${label}`}>
              <FileWarning className="w-3.5 h-3.5 mr-1" /> Mark Unclear
            </Button>
          </div>
        </div>
        <img src={src} alt={label} className="w-full max-h-56 object-contain bg-white" />
      </div>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
          <img src={src} alt={label} className="max-h-[70vh] object-contain mx-auto" />
        </DialogContent>
      </Dialog>
    </>
  );
}

function DetailModal({ customer, onClose, onChanged }) {
  const [orders, setOrders] = useState([]);
  const [editLimit, setEditLimit] = useState(false);
  const [newLimit, setNewLimit] = useState(customer?.credit_limit || 50000);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [unclearOpen, setUnclearOpen] = useState(null);
  const [unclearReason, setUnclearReason] = useState('');
  const [busy, setBusy] = useState(false);
  const c = customer || {};
  const status = c.status || 'unknown';
  const limit = Number(c.credit_limit || 0);
  const outstanding = Number(c.outstanding_amount || 0);
  const available = Math.max(0, limit - outstanding);
  const lat = c.latitude ?? c.geo_lat;
  const lng = c.longitude ?? c.geo_lng;
  const tradeLicense = resolveImage(c.trade_license_image || c.trade_license_base64);
  const storePhoto = resolveImage(c.store_photo || c.store_photo_base64);

  useEffect(() => {
    if (!c.id) return;
    axios.get(`${API}/customers/${c.id}/orders`, { headers: authHeaders() })
      .then(r => setOrders(r.data || []))
      .catch(() => {});
  }, [c.id]);

  const action = async (path, body) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/customers/${c.id}/${path}`, body || {}, { headers: authHeaders() });
      toast.success('Updated');
      onChanged(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setBusy(false); }
  };

  const saveLimit = async () => {
    if (!newLimit || Number(newLimit) < 0) { toast.warning('Enter a valid limit'); return; }
    await action('set-credit-limit', { credit_limit: Number(newLimit) });
    setEditLimit(false);
  };

  const submitBlock = async () => {
    if (!blockReason.trim()) { toast.warning('Enter a reason'); return; }
    await action('block', { reason: blockReason.trim() });
    setBlockOpen(false); setBlockReason('');
  };

  const submitUnclear = async () => {
    if (!unclearReason.trim()) { toast.warning('Enter a reason'); return; }
    await action('mark-document-unclear', { document: unclearOpen, reason: unclearReason.trim() });
    setUnclearOpen(null); setUnclearReason('');
  };

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="og-customer-detail-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-2xl">{c.business_name || c.name || '(Unnamed Retailer)'}</DialogTitle>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className="bg-orange-500 text-white">OG App Retailer</Badge>
                <Badge className={`border ${STATUS_BADGE[status] || 'bg-slate-100'}`}>
                  {status.replace(/_/g, ' ')}
                </Badge>
                {c.is_blocked && <Badge className="bg-zinc-200 text-zinc-800">BLOCKED</Badge>}
                <span className="text-xs text-slate-500">
                  Signed up: {new Date(c.signup_date || c.app_signup_date || c.created_at || Date.now()).toLocaleString()}
                </span>
                {c.kyc_submitted_at && (
                  <span className="text-xs text-slate-500">
                    KYC: {new Date(c.kyc_submitted_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {status === 'pending_approval' && (
                <Button className="bg-emerald-600" onClick={() => action('approve', { credit_limit: newLimit })} disabled={busy}>
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                </Button>
              )}
              {status === 'pending_approval' && (
                <Button variant="outline" className="text-rose-600 border-rose-300"
                        onClick={() => action('reject', { rejection_reason: 'Rejected from detail view' })} disabled={busy}>
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
              )}
              {(status === 'active') && (
                <Button variant="outline" className="text-rose-600 border-rose-300"
                        onClick={() => setBlockOpen(true)} disabled={busy} data-testid="og-detail-block-btn">
                  <ShieldX className="w-4 h-4 mr-1" /> Block
                </Button>
              )}
              {status === 'blocked' && (
                <Button className="bg-emerald-600" onClick={() => action('unblock')} disabled={busy} data-testid="og-detail-unblock-btn">
                  <ShieldCheck className="w-4 h-4 mr-1" /> Unblock
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-5 mt-3">
          {/* Business Info */}
          <Card>
            <CardContent className="p-4 space-y-2 text-sm">
              <h3 className="font-semibold text-slate-800 mb-2">Business Information</h3>
              <Row label="Business Name" value={c.business_name || c.name} />
              <Row label="GSTIN" value={c.gstin || c.gst} copyable={!!(c.gstin || c.gst)} />
              <Row label="Phone" value={c.phone || c.mobile} hrefTel={c.phone || c.mobile} />
              <Row label="Email" value={c.email} />
              <Row label="Segment" value={c.business_segment} />
              <Row label="Contact Person" value={c.contact_person} />
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardContent className="p-4 space-y-2 text-sm">
              <h3 className="font-semibold text-slate-800 mb-2">Store Location</h3>
              <Row label="Address" value={c.shop_address || c.address} multiline />
              {lat && lng && (
                <Row label="Coords"
                     value={`${lat}, ${lng}`}
                     hrefMaps={`https://maps.google.com/?q=${lat},${lng}`} />
              )}
              <Row label="City" value={c.city} />
              <Row label="State" value={c.state} />
              <Row label="Pincode" value={c.pincode} />
            </CardContent>
          </Card>

          {/* Credit */}
          <Card className="md:col-span-2">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> Credit Management
                </h3>
                {!editLimit && (
                  <Button size="sm" variant="outline" onClick={() => { setEditLimit(true); setNewLimit(limit); }}
                          data-testid="og-detail-edit-limit-btn">
                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Set / Increase Limit
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="border rounded p-3 bg-blue-50">
                  <div className="text-xs text-slate-500">Credit Limit</div>
                  <div className="text-2xl font-bold text-blue-700" data-testid="og-detail-credit-limit">{inr(limit)}</div>
                </div>
                <div className="border rounded p-3 bg-amber-50">
                  <div className="text-xs text-slate-500">Outstanding</div>
                  <div className="text-2xl font-bold text-amber-700">{inr(outstanding)}</div>
                </div>
                <div className="border rounded p-3 bg-emerald-50">
                  <div className="text-xs text-slate-500">Available</div>
                  <div className="text-2xl font-bold text-emerald-700">{inr(available)}</div>
                </div>
              </div>
              {editLimit && (
                <div className="flex gap-2 items-center">
                  <Input type="number" value={newLimit} onChange={(e) => setNewLimit(e.target.value)}
                         data-testid="og-detail-credit-limit-input" />
                  <Button onClick={saveLimit} disabled={busy} data-testid="og-detail-credit-limit-save">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                  <Button variant="outline" onClick={() => setEditLimit(false)}>Cancel</Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* KYC Documents */}
          <Card className="md:col-span-2">
            <CardContent className="p-4 space-y-3">
              <h3 className="font-semibold text-slate-800">KYC Documents</h3>
              <div className="grid md:grid-cols-2 gap-3">
                <KycDocPreview label="Trade License" src={tradeLicense}
                               onMarkUnclear={() => setUnclearOpen('trade_license')} />
                <KycDocPreview label="Store Photo" src={storePhoto}
                               onMarkUnclear={() => setUnclearOpen('store_photo')} />
              </div>
              {(c.documents_flagged || []).length > 0 && (
                <div className="text-xs text-amber-700 space-y-1 pt-2 border-t">
                  <div className="font-medium">Previously flagged:</div>
                  {c.documents_flagged.map((f, i) => (
                    <div key={i}>• {f.document}: {f.reason} <span className="text-slate-400">({new Date(f.at).toLocaleString()})</span></div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order History */}
          <Card className="md:col-span-2">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-800 mb-2">Order History ({orders.length})</h3>
              {orders.length === 0 ? (
                <div className="text-sm text-slate-400">No orders yet from this retailer.</div>
              ) : (
                <table className="w-full text-xs border">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-2 py-1">Order #</th>
                      <th className="text-left px-2 py-1">Date</th>
                      <th className="text-right px-2 py-1">Items</th>
                      <th className="text-right px-2 py-1">Total</th>
                      <th className="text-left px-2 py-1">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(o => (
                      <tr key={o.id} className="border-t">
                        <td className="px-2 py-1 font-mono">{o.order_number}</td>
                        <td className="px-2 py-1">{new Date(o.created_at).toLocaleDateString()}</td>
                        <td className="px-2 py-1 text-right">{(o.items || []).length}</td>
                        <td className="px-2 py-1 text-right">{inr(o.total)}</td>
                        <td className="px-2 py-1">{(o.status || '').replace(/_/g, ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Block modal */}
        <Dialog open={blockOpen} onOpenChange={(o) => !o && setBlockOpen(false)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Block {c.business_name || c.name}</DialogTitle></DialogHeader>
            <Textarea value={blockReason} onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Reason for blocking" rows={3} data-testid="og-block-reason-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setBlockOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={submitBlock} disabled={busy}
                      data-testid="og-block-confirm-btn">Block</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Unclear modal */}
        <Dialog open={!!unclearOpen} onOpenChange={(o) => !o && setUnclearOpen(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Mark {unclearOpen?.replace(/_/g, ' ')} as unclear</DialogTitle></DialogHeader>
            <Textarea value={unclearReason} onChange={(e) => setUnclearReason(e.target.value)}
                      placeholder="e.g. License image blurry, can't read GST number"
                      rows={3} data-testid="og-unclear-reason-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setUnclearOpen(null)}>Cancel</Button>
              <Button variant="destructive" onClick={submitUnclear} disabled={busy}
                      data-testid="og-unclear-confirm-btn">Send to Retailer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, copyable, hrefTel, hrefMaps, multiline }) {
  if (!value) return (
    <div className="flex gap-2"><span className="text-slate-400 w-28 text-xs">{label}</span><span className="text-slate-300">—</span></div>
  );
  return (
    <div className={`flex gap-2 ${multiline ? 'items-start' : 'items-center'}`}>
      <span className="text-slate-400 w-28 text-xs">{label}</span>
      <span className={`flex-1 ${multiline ? 'whitespace-pre-line' : ''}`}>
        {hrefTel && <a href={`tel:${hrefTel}`} className="text-blue-600 hover:underline">{value}</a>}
        {hrefMaps && <a href={hrefMaps} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">{value} <ExternalLink className="w-3 h-3" /></a>}
        {!hrefTel && !hrefMaps && value}
      </span>
      {copyable && <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => copy(value)}><Copy className="w-3 h-3" /></Button>}
    </div>
  );
}

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'pending_approval', label: 'Pending' },
  { key: 'active', label: 'Active' },
  { key: 'documents_pending', label: 'Docs Pending' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'rejected', label: 'Rejected' },
];

export default function OGOnboarding() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [detailOpen, setDetailOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers?source=og_retailer_app`, { headers: authHeaders() });
      setCustomers(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load OG onboarding');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'all' ? customers : customers.filter(c => c.status === filter);
  const counts = STATUS_FILTERS.reduce((acc, f) => {
    acc[f.key] = f.key === 'all' ? customers.length : customers.filter(c => c.status === f.key).length;
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-4" data-testid="og-onboarding-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">OG Onboarding</h1>
          <p className="text-sm text-slate-500 mt-1">
            Retailers pushed from the OG Retailer mobile app — click any card to review KYC & manage credit.
          </p>
        </div>
        <Badge className="bg-orange-500 hover:bg-orange-500 text-white" data-testid="og-source-badge">FROM OG APP</Badge>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`og-onboarding-filter-${f.key}`}
                  className={`px-3 py-1.5 text-sm rounded-full border transition ${
                    filter === f.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}>
            {f.label} <span className="ml-1 opacity-70">{counts[f.key] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="text-center text-slate-500 py-12">No OG retailers in this view.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(c => (
            <Card key={c.id} className="border-l-4 border-l-orange-400 cursor-pointer hover:shadow-md transition"
                  onClick={() => setDetailOpen(c)} data-testid={`og-customer-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-semibold">{c.business_name || c.name || '(Unnamed)'}</div>
                  <Badge className={`border text-xs ${STATUS_BADGE[c.status] || 'bg-slate-100'}`}
                         data-testid={`og-customer-status-${c.id}`}>
                    {(c.status || 'unknown').replace(/_/g, ' ')}
                  </Badge>
                </div>
                <Badge className="bg-orange-500 hover:bg-orange-500 text-white text-xs mb-2">OG App Retailer</Badge>
                <div className="text-xs text-slate-600 space-y-1">
                  <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{c.phone || c.mobile || '—'}</div>
                  {c.gstin && <div className="flex items-center gap-1"><FileBadge className="w-3 h-3 text-slate-400" />{c.gstin}</div>}
                  {(c.shop_address || c.address) && (
                    <div className="flex items-start gap-1 text-slate-500">
                      <MapPin className="w-3 h-3 text-slate-400 mt-0.5" />
                      <span className="line-clamp-2">{c.shop_address || c.address}</span>
                    </div>
                  )}
                  {!!c.credit_limit && (
                    <div className="text-emerald-700 font-medium">Limit: {inr(c.credit_limit)}</div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {detailOpen && (
        <DetailModal
          customer={detailOpen}
          onClose={() => { setDetailOpen(null); load(); }}
          onChanged={(updated) => { setDetailOpen(updated); load(); }}
        />
      )}
    </div>
  );
}
